import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { 
  Upload, 
  Download, 
  Users, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Plus,
  Trash2,
  Smartphone,
  DollarSign,
  Clock,
  RefreshCw,
  Eye,
  ExternalLink,
  Filter,
  Search,
  Calendar,
  Info,
  Loader
} from "lucide-react";
import bulkRechargeService from "../../services/bulkrechargeService";
import { toast } from "react-toastify";
import * as XLSX from 'xlsx';
import { useTranslation } from "react-i18next";

function BulkRecharge() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const userInfo = useSelector((state) => state.auth.user);
  const fileInputRef = useRef(null);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [manualEntries, setManualEntries] = useState([]);
  const [newEntry, setNewEntry] = useState({ 
    receiver: "", 
    amount: "", 
    operator: "" 
  });
  const [transferMode, setTransferMode] = useState("excel");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [jobResult, setJobResult] = useState(null);
  const [reportDownloadUrl, setReportDownloadUrl] = useState(null);
  const [activeTab, setActiveTab] = useState("upload");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({
    start: "",
    end: ""
  });
  const [pollingJobs, setPollingJobs] = useState({});
  const [jobStatuses, setJobStatuses] = useState({});

  const operators = [
    { value: "AWCC", label: t('bulkRecharge.operators.awcc'), color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
    { value: "Roshan", label: t('bulkRecharge.operators.roshan'), color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" },
    { value: "Etisalat", label: t('bulkRecharge.operators.etisalat'), color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200" },
    { value: "SALAAM", label: t('bulkRecharge.operators.salaam'), color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" },
    { value: "AFTMOBILE", label: t('bulkRecharge.operators.aftMobile'), color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" },
  ];

  const prefixToOperator = {
    "070": "AWCC", "071": "AWCC", "072": "AWCC",
    "073": "Roshan", "074": "Roshan",
    "075": "Etisalat", "076": "Etisalat", "078": "Etisalat",
    "077": "AFTMOBILE",
    "079": "Roshan"
  };

  const { data: walletData, isLoading: walletLoading, refetch: refetchWallet } = useQuery({
    queryKey: ["agentWallet"],
    queryFn: () => bulkRechargeService.getAgentWallet(),
    enabled: !!userInfo,
  });

  const { 
    data: rechargeHistory, 
    isLoading: historyLoading, 
    refetch: refetchHistory,
    error: historyError
  } = useQuery({
    queryKey: ["bulkRechargeHistory", searchTerm, dateRange.start, dateRange.end],
    queryFn: () => bulkRechargeService.getBulkRechargeHistory({
      search: searchTerm,
      startDate: dateRange.start,
      endDate: dateRange.end
    }),
    enabled: !!userInfo,
  });

  const bulkRechargeMutation = useMutation({
    mutationFn: (formData) => bulkRechargeService.bulkRecharge(formData),
    onSuccess: (response) => {
      console.log("Bulk recharge response:", response);
      
      if (response.data?.jobId) {
        toast.success(t('bulkRecharge.toasts.jobQueued'));
        toast.info(t('bulkRecharge.toasts.processingInfo'));
        
        startPollingJobStatus(response.data.jobId);
        
        setPollingJobs(prev => ({
          ...prev,
          [response.data.jobId]: {
            id: response.data.jobId,
            filename: response.data.filename,
            startTime: new Date().toISOString()
          }
        }));
      } else if (response.data?.status === "completed") {
        handleJobCompletion(response.data);
      }
      
      setSelectedFile(null);
      setManualEntries([]);
      setNewEntry({ receiver: "", amount: "", operator: "" });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      
      refetchWallet();
      refetchHistory();
    },
    onError: (error) => {
      console.error("Bulk recharge error:", error);
      toast.error(error?.response?.data?.error || t('bulkRecharge.toasts.processFailed'));
    },
  });

  const startPollingJobStatus = (jobId) => {
    if (pollingJobs[jobId]) return; 

    const interval = setInterval(async () => {
      try {
        const response = await bulkRechargeService.checkJobStatus(jobId);
        const status = response.data;
        
        console.log(`Job ${jobId} status:`, status);
        
        setJobStatuses(prev => ({
          ...prev,
          [jobId]: status
        }));

        if (status.data?.state === "completed" || status.data?.state === "failed") {
          clearInterval(interval);
  
          setPollingJobs(prev => {
            const newJobs = { ...prev };
            delete newJobs[jobId];
            return newJobs;
          });

          if (status.data?.result) {
            handleJobCompletion(status.data.result);
          }

          refetchHistory();
        }
      } catch (error) {
        console.error(`Error polling job ${jobId}:`, error);
        clearInterval(interval);
        
        setPollingJobs(prev => {
          const newJobs = { ...prev };
          delete newJobs[jobId];
          return newJobs;
        });
      }
    }, 3000);

    return () => clearInterval(interval);
  };

  const handleJobCompletion = (result) => {
    setJobResult(result);
    setShowSuccessModal(true);
    
    if (result.reportFileName) {
      setReportDownloadUrl(
        bulkRechargeService.getReportDownloadUrl(result.reportFileName)
      );
    }
    
    toast.success(
      t('bulkRecharge.toasts.completed', { 
        success: result.successCount || 0, 
        failed: result.failedCount || 0 
      })
    );
    
    refetchWallet();
    refetchHistory();
  };

  const downloadReport = async (filename) => {
    try {
      await bulkRechargeService.downloadReport(filename);
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error(t('bulkRecharge.toasts.reportFailed'));
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/octet-stream'
      ];
      
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
        toast.error(t('bulkRecharge.toasts.invalidFile'));
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('bulkRecharge.toasts.fileTooLarge'));
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      setSelectedFile(file);
    }
  };

  const detectOperator = (phoneNumber) => {
    if (!phoneNumber) return "";
    const numStr = phoneNumber.toString().trim();
    const prefix = numStr.slice(0, 3);
    return prefixToOperator[prefix] || "";
  };

  const handlePhoneNumberChange = (e) => {
    const phoneNumber = e.target.value.replace(/\D/g, '').slice(0, 10);
    setNewEntry(prev => {
      const operator = detectOperator(phoneNumber);
      return { ...prev, receiver: phoneNumber, operator };
    });
  };

  const handleManualAdd = () => {
    const phoneRegex = /^07\d{8}$/;
    
    if (!newEntry.receiver || !newEntry.amount || !newEntry.operator) {
      toast.error(t('bulkRecharge.toasts.allFieldsRequired'));
      return;
    }

    if (!phoneRegex.test(newEntry.receiver)) {
      toast.error(t('bulkRecharge.toasts.invalidPhone'));
      return;
    }

    const amount = parseFloat(newEntry.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error(t('bulkRecharge.toasts.invalidAmount'));
      return;
    }

    if (amount > 10000) {
      toast.error(t('bulkRecharge.toasts.amountExceeded'));
      return;
    }

    if (manualEntries.some(entry => entry.receiver === newEntry.receiver)) {
      toast.error(t('bulkRecharge.toasts.duplicatePhone'));
      return;
    }

    setManualEntries(prev => [...prev, {
      ...newEntry,
      amount: amount,
      receiver: newEntry.receiver.trim()
    }]);
    setNewEntry({ receiver: "", amount: "", operator: "" });
  };


  const removeManualEntry = (index) => {
    setManualEntries(prev => prev.filter((_, i) => i !== index));
  };

  const downloadTemplate = async () => {
    try {
      await bulkRechargeService.downloadTemplate();
      toast.success(t('bulkRecharge.toasts.templateDownloaded'));
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error(t('bulkRecharge.toasts.templateFailed'));
    }
  };

  const handleSubmit = async () => {
    if (bulkRechargeMutation.isPending) return;

    try {
      if (transferMode === "excel") {
        if (!selectedFile) {
          toast.error(t('bulkRecharge.toasts.selectFile'));
          return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);
        
        bulkRechargeMutation.mutate(formData);
        
      } else {
        if (manualEntries.length === 0) {
          toast.error(t('bulkRecharge.toasts.addEntries'));
          return;
        }

        const totalAmount = manualEntries.reduce((sum, entry) => sum + entry.amount, 0);
        const availableBalance = parseFloat(walletData?.data?.balance || 0);

        const worksheetData = manualEntries.map(entry => ({
          receiver: entry.receiver,
          amount: entry.amount,
          operator: entry.operator
        }));
        
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Recharges");
        
        const excelBuffer = XLSX.write(workbook, { 
          bookType: 'xlsx', 
          type: 'array' 
        });
        
        const file = new File([excelBuffer], "manual_recharges.xlsx", { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });

        const formData = new FormData();
        formData.append("file", file);
        bulkRechargeMutation.mutate(formData);
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(t('bulkRecharge.toasts.prepareFailed'));
    }
  };

  // Calculate totals
  const availableBalance = parseFloat(walletData?.data?.balance || 0).toFixed(2);
  const totalManualAmount = manualEntries.reduce((sum, entry) => sum + entry.amount, 0);
  const historyData = rechargeHistory?.data?.data || [];

  return (
    <div className="w-full px-5 py-4 min-h-screen h-auto bg-[linear-gradient(135deg,_#EFF2FF_0%,_#FAF5FF_50%,_#FCF3FB_100%)] dark:bg-[linear-gradient(135deg,_#1e293b_0%,_#334155_50%,_#0f172a_100%)]">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{t('bulkRecharge.title')}</h1>
        <p className="text-gray-600 dark:text-gray-300">{t('bulkRecharge.subtitle')}</p>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-1 mb-6">
        <div className="flex gap-1">
          {[
            { id: "upload", label: t('bulkRecharge.tabs.upload'), icon: Upload },
            { id: "history", label: t('bulkRecharge.tabs.history'), icon: Clock },
            { id: "instructions", label: t('bulkRecharge.tabs.instructions'), icon: Info }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-4 font-medium rounded-md transition-all ${
                activeTab === tab.id 
                  ? "bg-primary text-white shadow-md" 
                  : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <tab.icon size={18} />
                {tab.label}
              </div>
            </button>
          ))}
        </div>
      </div>

      {Object.keys(pollingJobs).length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-3 flex items-center gap-2">
            <Loader className="animate-spin" size={18} />
            {t('bulkRecharge.processingJobs', { count: Object.keys(pollingJobs).length })}
          </h3>
          <div className="space-y-2">
            {Object.values(pollingJobs).map((job) => {
              const status = jobStatuses[job.id];
              return (
                <div key={job.id} className="bg-white dark:bg-slate-800 p-3 rounded border border-yellow-100 dark:border-yellow-900">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white">{t('bulkRecharge.jobId')} #{job.id}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{job.filename}</p>
                    </div>
                    <div className="text-right">
                      {status?.data?.state === "completed" ? (
                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-xs">
                          {t('bulkRecharge.status.completed')}
                        </span>
                      ) : status?.data?.state === "failed" ? (
                        <span className="px-2 py-1 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-full text-xs">
                          {t('bulkRecharge.status.failed')}
                        </span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Loader className="animate-spin text-yellow-600 dark:text-yellow-400" size={16} />
                          <span className="text-sm text-yellow-600 dark:text-yellow-400">
                            {status?.data?.progress || 0}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {status?.data?.progress && (
                    <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div 
                        className="bg-yellow-500 h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${status.data.progress}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === "upload" && (
        <>
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{t('bulkRecharge.rechargeMethod')}</h2>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setTransferMode("excel")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                  transferMode === "excel" 
                    ? "bg-primary text-white border-primary shadow-md" 
                    : "bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-slate-600"
                }`}
              >
                <FileText size={20} />
                {t('bulkRecharge.methods.excelUpload')}
              </button>
              <button
                onClick={() => setTransferMode("manual")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                  transferMode === "manual" 
                    ? "bg-primary text-white border-primary shadow-md" 
                    : "bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-slate-600"
                }`}
              >
                <Smartphone size={20} />
                {t('bulkRecharge.methods.manualEntry')}
              </button>
            </div>
          </div>

          {transferMode === "excel" && (
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{t('bulkRecharge.uploadFile')}</h2>
              
              <div 
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center mb-4 hover:border-primary/80 transition-all cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    const event = { target: { files: [file] } };
                    handleFileSelect(event);
                  }
                }}
              >
                <Upload className="mx-auto text-gray-400 dark:text-gray-500 mb-3" size={48} />
                <p className="text-gray-600 dark:text-gray-300 mb-2">
                  {selectedFile ? selectedFile.name : t('bulkRecharge.dragDrop')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {t('bulkRecharge.fileFormat')}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary transition-colors"
                >
                  {t('bulkRecharge.browseFiles')}
                </button>
              </div>

              {selectedFile && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="text-green-600 dark:text-green-400" size={20} />
                      <div>
                        <p className="font-medium text-green-800 dark:text-green-200">{t('bulkRecharge.fileSelected')}</p>
                        <p className="text-sm text-green-600 dark:text-green-300">{selectedFile.name}</p>
                        <p className="text-xs text-green-500 dark:text-green-400">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedFile(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-full"
                    >
                      <XCircle size={20} />
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 px-4 py-2 text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition-colors"
              >
                <Download size={18} />
                {t('bulkRecharge.downloadTemplate')}
              </button>
            </div>
          )}

          {transferMode === "manual" && (
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{t('bulkRecharge.manualEntries')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('bulkRecharge.phoneNumber')}
                  </label>
                  <input
                    type="tel"
                    value={newEntry.receiver}
                    onChange={handlePhoneNumberChange}
                    placeholder={t('bulkRecharge.phonePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    maxLength={10}
                  />
                  {newEntry.receiver && (
                    <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                      {t('bulkRecharge.detected')}: {detectOperator(newEntry.receiver) || t('bulkRecharge.unknownOperator')}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('bulkRecharge.amount')}
                  </label>
                  <input
                    type="number"
                    value={newEntry.amount}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder={t('bulkRecharge.amountPlaceholder')}
                    min="10"
                    max="10000"
                    step="10"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('bulkRecharge.operator')}
                  </label>
                  <select
                    value={newEntry.operator}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, operator: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                  >
                    <option value="">{t('bulkRecharge.selectOperator')}</option>
                    {operators.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={handleManualAdd}
                    className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors w-full justify-center"
                  >
                    <Plus size={18} />
                    {t('bulkRecharge.addEntry')}
                  </button>
                </div>
              </div>

              {manualEntries.length > 0 && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 dark:bg-slate-700 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h3 className="font-medium text-gray-700 dark:text-gray-200">
                      {t('bulkRecharge.rechargeList', { count: manualEntries.length })}
                    </h3>
                    <div className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                      {t('bulkRecharge.total')}: {totalManualAmount.toFixed(2)} AFN
                    </div>
                  </div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-64 overflow-y-auto">
                    {manualEntries.map((entry, index) => {
                      const operatorColor = operators.find(op => op.value === entry.operator)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
                      return (
                        <div key={index} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/50">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                              <Smartphone size={16} className="text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800 dark:text-white">{entry.receiver}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`px-2 py-0.5 rounded-full text-xs ${operatorColor}`}>
                                  {entry.operator}
                                </span>
                                <span className="text-sm text-gray-600 dark:text-gray-300">{entry.amount} AFN</span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => removeManualEntry(index)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-full"
                            title={t('common.remove')}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}


          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={bulkRechargeMutation.isPending || 
                (transferMode === "excel" && !selectedFile) ||
                (transferMode === "manual" && manualEntries.length === 0)}
              className="bg-primary text-white px-8 py-3 rounded-lg hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-md"
            >
              {bulkRechargeMutation.isPending ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  {t('common.processing')}
                </>
              ) : (
                <>
                  <Upload size={18} />
                  {t('bulkRecharge.processButton')}
                </>
              )}
            </button>
          </div>
        </>
      )}

      {activeTab === "history" && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{t('bulkRecharge.historyTitle')}</h2>
            <button
              onClick={() => refetchHistory()}
              className="flex items-center gap-2 px-3 py-1 text-sm text-primary hover:bg-blue-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              disabled={historyLoading}
            >
              <RefreshCw size={16} className={historyLoading ? "animate-spin" : ""} />
              {t('common.refresh')}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('common.search')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('bulkRecharge.searchPlaceholder')}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('common.startDate')}
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18} />
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('common.endDate')}
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18} />
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* History Table */}
          {historyLoading ? (
            <div className="text-center py-12">
              <Loader className="animate-spin text-blue-600 dark:text-blue-400 mx-auto mb-4" size={48} />
              <p className="text-gray-600 dark:text-gray-300">{t('common.loading')}</p>
            </div>
          ) : historyError ? (
            <div className="text-center py-12 text-red-600 dark:text-red-400">
              <XCircle className="mx-auto mb-4" size={48} />
              <p>{t('common.errorLoading')}</p>
            </div>
          ) : historyData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-slate-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('bulkRecharge.columns.dateTime')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('bulkRecharge.columns.fileName')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('bulkRecharge.columns.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('bulkRecharge.columns.successful')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('bulkRecharge.columns.failed')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('bulkRecharge.columns.totalAmount')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('bulkRecharge.columns.report')}
                    </th>
                  </tr>
                </thead>
               // Fixed History Table section - replace the entire table body section

<tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-gray-700">
  {historyData.map((record, index) => (
    <tr key={record.id || index} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
        {new Date(record.createdAt).toLocaleString()}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
        {record.meta?.filename || record.fileName || '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          record.status === 'succeeded' || record.status === 'completed'
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            : record.status === 'pending' || record.status === 'processing'
            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
        }`}>
          {t(`bulkRecharge.status.${record.status || 'pending'}`)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
        <div className="flex items-center gap-1">
          <CheckCircle className="text-green-500 dark:text-green-400" size={16} />
          {record.meta?.successCount || record.successCount || 0}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
        <div className="flex items-center gap-1">
          <XCircle className="text-red-500 dark:text-red-400" size={16} />
          {record.meta?.failedCount || record.failedCount || 0}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200 font-medium">
        {parseFloat(record.amount || record.totalAmount || 0).toFixed(2)} AFN
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        {(record.meta?.reportFileName || record.reportFileName) && (
          <button
            onClick={() => downloadReport(
              record.meta?.reportFileName || record.reportFileName
            )}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 p-1 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-full transition-colors"
            title={t('common.download')}
          >
            <Download size={18} />
          </button>
        )}
      </td>
    </tr>
  ))}
</tbody>
               </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <FileText className="mx-auto text-gray-300 dark:text-gray-600 mb-3" size={48} />
              <p>{t('bulkRecharge.noHistory')}</p>
              <p className="text-sm mt-1">{t('bulkRecharge.noHistoryHint')}</p>
            </div>
          )}
        </div>
      )}

      {activeTab === "instructions" && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800 dark:text-white">
            <Info className="text-blue-600 dark:text-blue-400" size={20} />
            {t('bulkRecharge.instructions.title')}
          </h2>
          
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-2">
                <FileText size={18} />
                {t('bulkRecharge.instructions.fileFormat')}
              </h3>
              <ul className="text-blue-700 dark:text-blue-300 space-y-1 ltr:ml-6 rtl:mr-6 list-disc">
                <li>{t('bulkRecharge.instructions.format1')}</li>
                <li>{t('bulkRecharge.instructions.format2')}</li>
                <li>{t('bulkRecharge.instructions.format3')}</li>
                <li>{t('bulkRecharge.instructions.format4')}</li>
              </ul>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2 flex items-center gap-2">
                <CheckCircle size={18} />
                {t('bulkRecharge.instructions.validationRules')}
              </h3>
              <ul className="text-green-700 dark:text-green-300 space-y-1 ltr:ml-6 rtl:mr-6 list-disc">
                <li>{t('bulkRecharge.instructions.rule1')}</li>
                <li>{t('bulkRecharge.instructions.rule2')}</li>
                <li>{t('bulkRecharge.instructions.rule3')}</li>
                <li>{t('bulkRecharge.instructions.rule4')}</li>
              </ul>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg p-4">
              <h3 className="font-semibold text-purple-800 dark:text-purple-200 mb-2 flex items-center gap-2">
                <Smartphone size={18} />
                {t('bulkRecharge.instructions.prefixes')}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { prefix: "070, 071", operator: t('bulkRecharge.operators.awcc') },
                  { prefix: "072, 079", operator: t('bulkRecharge.operators.roshan') },
                  { prefix: "073, 078", operator: t('bulkRecharge.operators.etisalat') },
                  { prefix: "077, 076", operator: t('bulkRecharge.operators.atoma') },
                  { prefix: "074", operator: t('bulkRecharge.operators.salaam') }
                ].map((item, idx) => (
                  <div key={idx} className="bg-white dark:bg-slate-700 p-3 rounded border border-purple-200 dark:border-purple-700">
                    <p className="font-medium text-gray-800 dark:text-white">{item.prefix}</p>
                    <p className="text-sm text-purple-600 dark:text-purple-300">{item.operator}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2 flex items-center gap-2">
                <AlertCircle size={18} />
                {t('bulkRecharge.instructions.importantNotes')}
              </h3>
              <ul className="text-yellow-700 dark:text-yellow-300 space-y-1 ltr:ml-6 rtl:mr-6 list-disc">
                <li>{t('bulkRecharge.instructions.note1')}</li>
                <li>{t('bulkRecharge.instructions.note2')}</li>
                <li>{t('bulkRecharge.instructions.note3')}</li>
                <li>{t('bulkRecharge.instructions.note4')}</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && jobResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-green-600 dark:text-green-400" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white">{t('bulkRecharge.modal.title')}</h3>
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center py-2 border-b dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-300">{t('bulkRecharge.modal.successful')}:</span>
                <span className="font-semibold text-green-600 dark:text-green-400">{jobResult.successCount || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-300">{t('bulkRecharge.modal.failed')}:</span>
                <span className="font-semibold text-red-600 dark:text-red-400">{jobResult.failedCount || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-300">{t('bulkRecharge.modal.invalid')}:</span>
                <span className="font-semibold text-yellow-600 dark:text-yellow-400">{jobResult.invalidCount || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-300">{t('bulkRecharge.modal.totalAmount')}:</span>
                <span className="font-semibold text-gray-900 dark:text-white">{parseFloat(jobResult.totalAmount || 0).toFixed(2)} AFN</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600 dark:text-gray-300">{t('bulkRecharge.modal.usedBalance')}:</span>
                <span className="font-semibold text-blue-600 dark:text-blue-400">{parseFloat(jobResult.totalUsedAmount || 0).toFixed(2)} AFN</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                {t('common.close')}
              </button>
              {reportDownloadUrl && (
                <button
                  onClick={() => window.open(reportDownloadUrl, '_blank')}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors"
                >
                  <Download size={18} />
                  {t('bulkRecharge.modal.downloadReport')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BulkRecharge;